import { useState, useEffect } from 'react';
import { api } from '../../api/api';

interface GiveawayAttempt {
  id: number;
  user_id: number;
  reward_id: string;
  marketor_id: number;
  twitch_id: string;
  refid: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface MarketorGiveawayAttemptsModalProps {
  isOpen: boolean;
  onClose: () => void;
  marketorId: number;
}

const MarketorGiveawayAttemptsModal = ({ isOpen, onClose, marketorId }: MarketorGiveawayAttemptsModalProps) => {
  const [giveawayAttempts, setGiveawayAttempts] = useState<GiveawayAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGiveawayAttempts = async () => {
      try {
        setLoading(true);
        // Assuming there's an endpoint to filter giveaway attempts by marketor_id
        // If not, we might need to fetch all and filter on the frontend, or create a new backend endpoint.
        // For now, I'll assume a filterable endpoint exists or fetch all and filter.
        // Let's assume the /api/all/giveaway_attempts endpoint can take a query parameter for marketor_id
        const response = await api.get(`/api/all/giveaway_attempts?marketor_id=${marketorId}`);
        // If the backend doesn't support filtering by marketor_id, we'd filter here:
        // const filteredAttempts = response.data.filter((attempt: GiveawayAttempt) => attempt.marketor_id === marketorId);
        setGiveawayAttempts(response.data); // Assuming filtered data from backend
      } catch (err: any) {
        setError(err.message || 'Failed to fetch giveaway attempts');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && marketorId) {
      fetchGiveawayAttempts();
    }
  }, [isOpen, marketorId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-1000 p-4">
      <div className="gothic-panel w-full max-w-5xl max-h-[95vh] flex flex-col">
        <h2 className="gothic-heading text-center mb-6 text-3xl">Giveaway Attempts for Marketor ID: {marketorId}</h2>
        {loading && <p className="gothic-text-silver text-center text-lg">Loading giveaway attempts...</p>}
        {error && <p className="text-red-500 text-center text-lg">Error: {error}</p>}
        {!loading && !error && giveawayAttempts.length === 0 && (
          <p className="gothic-text-silver text-center text-lg">No giveaway attempts found for this marketor.</p>
        )}
        {!loading && !error && giveawayAttempts.length > 0 && (
          <div className="flex-grow overflow-y-auto overflow-x-auto">
            <table className="min-w-full gothic-panel border-collapse">
              <thead>
                <tr className="bg-gray-800">
                  <th className="py-4 px-4 text-left gothic-text-silver text-base">ID</th>
                  <th className="py-4 px-4 text-left gothic-text-silver text-base">User ID</th>
                  <th className="py-4 px-4 text-left gothic-text-silver text-base">Reward ID</th>
                  <th className="py-4 px-4 text-left gothic-text-silver text-base">Twitch ID</th>
                  <th className="py-4 px-4 text-left gothic-text-silver text-base">Ref ID</th>
                  <th className="py-4 px-4 text-left gothic-text-silver text-base">Status</th>
                  <th className="py-4 px-4 text-left gothic-text-silver text-base">Created At</th>
                </tr>
              </thead>
              <tbody>
                {giveawayAttempts.map((attempt) => (
                  <tr key={attempt.id} className="border-t border-gray-700 hover:bg-gray-800 transition duration-200 cursor-pointer">
                    <td className="py-4 px-4 gothic-text-gold text-base">{attempt.id}</td>
                    <td className="py-4 px-4 gothic-text-silver text-base">{attempt.user_id}</td>
                    <td className="py-4 px-4 gothic-text-silver text-base">{attempt.reward_id}</td>
                    <td className="py-4 px-4 gothic-text-silver text-base">{attempt.twitch_id}</td>
                    <td className="py-4 px-4 gothic-text-silver text-base">{attempt.refid}</td>
                    <td className="py-4 px-4 gothic-text-silver text-base">{attempt.status}</td>
                    <td className="py-4 px-4 gothic-text-silver text-base">{new Date(attempt.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="gothic-button gothic-button-primary px-6 py-3 text-lg">Close</button>
        </div>
      </div>
    </div>
  );
};

export default MarketorGiveawayAttemptsModal;
